import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { Button, IconButton, Stack, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { useFormikContext } from "formik";
import { AskWhen, AskWhom } from "../../../clients/graphqlTypes.js";
import { SelectMultipleField } from "../../commons/form-fields/SelectMultipleField.js";
import { useQuestionContol } from "./useQuestionContol.js";

export interface QuestionControlFormWithOverrideFormValues {
  questionControl: Array<{
    askWhom: AskWhom[];
    askWhen: AskWhen[];
  }> | null;
}

interface Props {
  property: {
    questionControl: Array<{
      askWhom: AskWhom[];
      askWhen: AskWhen[];
    }>;
  };
  allowedAskWhen?: AskWhen[];
  disabled?: boolean;
}

export const QuestionControlFormWithOverride = ({
  property,
  allowedAskWhen,
  disabled,
}: Props) => {
  const { t } = useTranslate(["QuoteItem", "Global"]);
  const { askWhomOptions, askWhenOptions } = useQuestionContol(allowedAskWhen);
  const formikProps =
    useFormikContext<QuestionControlFormWithOverrideFormValues>();

  const questionControl =
    formikProps.values.questionControl ?? property.questionControl;

  return (
    <Stack direction="column" spacing={1}>
      {questionControl.map((qc, idx, arr) => {
        return (
          <div key={idx}>
            <Stack direction={"row"} spacing={1}>
              <SelectMultipleField
                name={`questionControl[${idx}].askWhen`}
                value={
                  !formikProps.values.questionControl
                    ? questionControl[idx].askWhen
                    : undefined
                }
                onChange={
                  !formikProps.values.questionControl
                    ? newValue => {
                        const newQuestionControl = [...questionControl].map(
                          (qc, i) => {
                            if (i === idx) {
                              return { ...qc, askWhen: newValue };
                            }
                            return qc;
                          }
                        );
                        formikProps.setFieldValue(
                          "questionControl",
                          newQuestionControl
                        );
                      }
                    : undefined
                }
                label={t("Ask when", { ns: "QuoteItem" })}
                options={askWhenOptions}
                disabled={disabled || formikProps.isSubmitting}
              />
              <SelectMultipleField
                name={`questionControl[${idx}].askWhom`}
                value={
                  !formikProps.values.questionControl
                    ? questionControl[idx].askWhom
                    : undefined
                }
                onChange={
                  !formikProps.values.questionControl
                    ? newValue => {
                        const newQuestionControl = [...questionControl].map(
                          (qc, i) => {
                            if (i === idx) {
                              return { ...qc, askWhom: newValue };
                            }
                            return qc;
                          }
                        );
                        formikProps.setFieldValue(
                          "questionControl",
                          newQuestionControl
                        );
                      }
                    : undefined
                }
                options={askWhomOptions}
                label={t("Ask whom", { ns: "QuoteItem" })}
                disabled={disabled || formikProps.isSubmitting}
              />
              <IconButton
                color="primary"
                sx={{ alignSelf: "flex-start" }}
                onClick={() => {
                  const newValue = [...questionControl];
                  newValue.splice(idx, 1);
                  formikProps.setFieldValue("questionControl", newValue);
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Stack>
            {idx < arr.length - 1 ? (
              <Typography
                variant="caption"
                sx={{
                  color: "grey.600",
                }}
              >
                {t("or", { ns: "Global" })}
              </Typography>
            ) : null}
          </div>
        );
      })}
      <Button
        variant="text"
        color="secondary"
        startIcon={<AddIcon />}
        sx={{ alignSelf: "flex-start" }}
        onClick={async () => {
          await formikProps.setFieldValue("questionControl", [
            ...questionControl,
            { askWhen: [], askWhom: [] },
          ]);
        }}
        disabled={disabled || formikProps.isSubmitting}
      >
        {t("Add", { ns: "Global" })}
      </Button>
    </Stack>
  );
};
